import React from "react";
import { Grid, Box, Typography } from "@mui/material";
import KpiCard from "./KpiCard";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { DashboardSummary } from "../../../services/api/dashboard/dashboardApi";

interface MetricsGridProps {
  summary: DashboardSummary | undefined;
  loading: boolean;
}

export default function MetricsGrid({ summary, loading }: MetricsGridProps) {
  // Função para formatar tempo em horas para exibição amigável
  const formatTime = (hours: number | null): string => {
    if (hours === null || hours === 0) return "N/A";
    
    if (hours < 24) {
      return `${hours.toFixed(1)}h`;
    } else {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      return `${days}d ${remainingHours.toFixed(0)}h`;
    }
  };

  // Função para formatar percentual
  const formatPercentage = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  // Função para formatar número com separador de milhares
  const formatNumber = (value: number): string => {
    return value.toLocaleString("pt-BR");
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* KPI 1: Total de Atividades */}
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard
            title="Total de Atividades"
            value={loading ? "..." : formatNumber(summary?.totalActivities || 0)}
            icon={<AssignmentTurnedInOutlinedIcon fontSize="large" />}
            variant="info"
            loading={loading}
          />
        </Grid>

        {/* KPI 2: Tempo Médio de Execução */}
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard
            title="Tempo Médio de Execução"
            value={loading ? "..." : formatTime(summary?.avgExecutionTime || 0)}
            subtitle="Período entre início e fim"
            icon={<TimerOutlinedIcon fontSize="large" />}
            variant="default"
            loading={loading}
          />
        </Grid>

        {/* KPI 3: Tempo Médio Total */}
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard
            title="Tempo Médio Total"
            value={loading ? "..." : formatTime(summary?.avgTotalTime || 0)}
            subtitle="Período desde criação até fim"
            icon={<AccessTimeOutlinedIcon fontSize="large" />}
            variant="default"
            loading={loading}
          />
        </Grid>

        {/* KPI 4: Taxa de Conclusão */}
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard
            title="Taxa de Conclusão"
            value={loading ? "..." : formatPercentage(summary?.completionRate || 0)}
            subtitle="Atividades concluídas"
            icon={<CheckCircleOutlinedIcon fontSize="large" />}
            variant="success"
            loading={loading}
          />
        </Grid>

        {/* KPI 5: Atividades Atrasadas */}
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard
            title="Atividades Atrasadas"
            value={
              loading
                ? "..."
                : `${formatNumber(summary?.overdueActivities || 0)} (${formatPercentage(
                    summary?.overduePercentage || 0
                  )})`
            }
            subtitle="Projetos com prazo vencido"
            icon={<WarningAmberOutlinedIcon fontSize="large" />}
            variant={(summary?.overduePercentage || 0) > 20 ? "error" : "warning"}
            loading={loading}
            isAlert={true}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

